/**
 * AirCargoMCP Developers / API docs, the air sibling of trackingmcp's DevelopersPage.
 *
 * The thesis is the API answering: a live terminal that runs GET /track/203-12345675 against the real
 * aircargomcp API and renders the { data: AwbTrackingData } response inline. Everything below documents
 * the REAL surface, /track/:awb (primary), /prefix/:code, POST /rates/quote, /aircraft/flight/:n, plus
 * the MCP tools (track_awb, resolve_awb_prefix, air_rate_quote). Token-driven off the navo24 brand vars
 * in index.css: dark hero island (.tone-dark) + cream light docs, so it flips theme for free.
 *
 * SEO: "air cargo tracking API", "AWB tracking API", "IATA Cargo-IMP API", "air freight rate API", "MCP".
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import coverage from '../../../api/src/data/air-coverage.generated.json'
import { Layout } from '../components/Layout'
import { PageMeta } from '../lib/meta'

// Interactive terminal target. Prod default per spec; VITE_API_BASE points it at localhost in dev.
const API = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.aircargomcp.com'

// The rich sample rendered by default so the terminal is never empty and never errors on first paint.
// This is the real, trimmed { data: AwbTrackingData } for the delivered Cebu Pacific AWB 203-12345675
// (MNL → CEB) — the harmonised GDS air-tracking shape: origin_location / destination_location geo,
// events with datetimes{estimated,actual} + a per-event location, one routes[] leg with the great-circle
// path polyline for maps, and the provider metadata block. Events + the path are trimmed for readability.
const DEMO_SAMPLE = `{
  "data": {
    "awb": "203-12345675",
    "airline_prefix": "203",
    "airline_iata": "5J",
    "airline_name": "Cebu Pacific",
    "status": "delivered",
    "origin": "MNL",
    "origin_name": "Ninoy Aquino International Airport",
    "origin_location": {
      "iata_code": "MNL", "icao_code": "RPLL", "name": "Ninoy Aquino International Airport",
      "city": "Manila", "country": "Philippines", "country_code": "PH",
      "lat": 14.5086, "lng": 121.02, "timezone": "Asia/Manila"
    },
    "destination": "CEB",
    "destination_name": "Mactan Cebu International Airport",
    "destination_location": {
      "iata_code": "CEB", "icao_code": "RPVM", "name": "Mactan Cebu International Airport",
      "city": "Lapu-Lapu City", "country": "Philippines", "country_code": "PH",
      "lat": 10.3075, "lng": 123.979, "timezone": "Asia/Manila"
    },
    "pieces": 1,
    "weight_kg": 5,
    "flight_number": "5J561",
    "departure": { "estimated": "2014-09-25T00:00:00Z", "actual": "2014-09-25T03:25:00Z", "datetime_precision": "datetime" },
    "arrival":   { "estimated": null, "actual": "2014-09-25T04:35:00Z", "datetime_precision": "datetime" },
    "eta": "2014-09-25T04:35:00Z",
    "eta_is_actual": true,
    "eta_precision": "datetime",
    "events": [
      {
        "status_code": "RCS", "event_family": "ACCEPTANCE", "is_actual": true,
        "datetime": "2014-09-25T00:18:00Z",
        "datetimes": { "estimated": null, "actual": "2014-09-25T00:18:00Z", "datetime_precision": "datetime" },
        "station": "MNL", "station_name": "Ninoy Aquino International Airport",
        "location": { "iata_code": "MNL", "icao_code": "RPLL", "lat": 14.5086, "lng": 121.02 },
        "pieces": 1, "weight_kg": 5, "description": "Received from shipper, 1 pc / 5 kg"
      },
      {
        "status_code": "DEP", "event_family": "FLIGHT", "is_actual": true,
        "datetime": "2014-09-25T03:25:00Z",
        "datetimes": { "estimated": null, "actual": "2014-09-25T03:25:00Z", "datetime_precision": "datetime" },
        "station": "MNL", "station_name": "Ninoy Aquino International Airport",
        "location": { "iata_code": "MNL", "icao_code": "RPLL", "lat": 14.5086, "lng": 121.02 },
        "flight_number": "5J561", "pieces": 1, "weight_kg": 5,
        "description": "Departed, 1 pc / 5 kg, flight: 5J561, to: CEB"
      },
      {
        "status_code": "DLV", "event_family": "DELIVERY", "is_actual": true,
        "datetime": "2014-09-25T13:24:00Z",
        "datetimes": { "estimated": null, "actual": "2014-09-25T13:24:00Z", "datetime_precision": "datetime" },
        "station": "CEB", "station_name": "Mactan Cebu International Airport",
        "location": { "iata_code": "CEB", "icao_code": "RPVM", "lat": 10.3075, "lng": 123.979 },
        "pieces": 1, "weight_kg": 5, "description": "Delivered, 1 pc / 5 kg"
      }
    ],
    "routes": [
      {
        "order_id": 1, "status": "ARRIVED", "transport_type": "PLANE",
        "from": { "iata_code": "MNL", "name": "Ninoy Aquino International Airport", "lat": 14.5086, "lng": 121.02 },
        "to":   { "iata_code": "CEB", "name": "Mactan Cebu International Airport", "lat": 10.3075, "lng": 123.979 },
        "departure": { "estimated": "2014-09-25T00:00:00Z", "actual": "2014-09-25T03:25:00Z", "datetime_precision": "datetime" },
        "arrival":   { "estimated": null, "actual": "2014-09-25T04:35:00Z", "datetime_precision": "datetime" },
        "flight_number": "5J561", "pieces": 1, "weight_kg": 5,
        "path": [ [14.5086, 121.02], [13.009, 122.43], [11.509, 123.46], [10.3075, 123.979] ]
      }
    ],
    "metadata": {
      "status_code": "OK",
      "airline": { "name": "Cebu Pacific", "prefix": "203", "iata_code": "5J", "icao_code": "CEB", "url": "https://www.cebupacificair.com" },
      "updated_at": "2026-07-30T20:18:12Z",
      "from_cache": false,
      "cache_expires": "2026-07-30T21:19:12Z"
    }
  }
}`

// ── Response schema (documents the REAL AwbTrackingData shape from packages/types/src/air-tracking.ts) ──
type Field = {
  name: string
  type: string
  desc: string
  example?: string
  enumDocs?: [string, string][]
  children?: Field[]
}

// AwbStatus, the normalised, cross-carrier lifecycle status.
const STATUS_DOCS: [string, string][] = [
  ['booked', 'The AWB exists and is booked, but the airline has not accepted the freight yet.'],
  ['accepted', 'Cargo received from the shipper at the origin station (RCS).'],
  ['in_transit', 'Airborne or moving between stations, departed, or at a transit hub.'],
  ['arrived', 'Landed at the destination station, not yet released to the consignee.'],
  ['available', 'Arrival notice sent (NFD), ready for pickup or delivery.'],
  ['delivered', 'Handed over to the consignee (DLV). End of the journey.'],
  ['unknown', 'Not enough milestone data yet to place the shipment in the flow.'],
]

// IATA Cargo-IMP status codes, the air analogue of DCSA event codes. Closed set airlines publish.
const AIR_STATUS_CODES: [string, string][] = [
  ['FOH', 'Freight on hand, booked and awaiting acceptance at origin.'],
  ['RCS', 'Received from shipper, cargo accepted at the origin station.'],
  ['MAN', 'Manifested, assigned to a specific flight.'],
  ['DEP', 'Departed, uplifted on the flight from a station.'],
  ['ARR', 'Arrived, landed at a transit or destination station.'],
  ['RCF', 'Received from flight, offloaded and received at the arrival station.'],
  ['TFD', 'Transferred, handed to another carrier on an interline move.'],
  ['NFD', 'Notified, arrival notice sent to the consignee (ready for pickup).'],
  ['AWD', 'Arrival documents delivered, paperwork handed to customs / broker.'],
  ['CCD', 'Customs cleared.'],
  ['DLV', 'Delivered to the consignee.'],
]

// AirEventFamily, the coarse grouping every status code rolls up to.
const FAMILY_DOCS: [string, string][] = [
  ['ACCEPTANCE', 'Origin handling: booking, freight-on-hand and acceptance (FOH, RCS).'],
  ['FLIGHT', 'The air leg: manifest, departure, arrival, offload (MAN, DEP, ARR, RCF, TFD).'],
  ['DELIVERY', 'Destination release: arrival notice and delivery (NFD, DLV).'],
  ['CUSTOMS', 'Customs and documents: docs delivered, cleared (AWD, CCD).'],
]

// Error codes returned by GET /track/:awb (packages/types AppError, see apps/api routes/services).
const ERROR_CODES: [string, string, string][] = [
  [
    'invalid_reference',
    '400',
    'The AWB is malformed, not an 11-digit prefix + serial, or the check digit fails.',
  ],
  [
    'unsupported_carrier',
    '404',
    'The 3-digit prefix resolves to an airline we have not wired a connector for yet.',
  ],
  ['not_found', '404', 'The airline returned no record for that AWB. Re-check the number.'],
  ['blocked', '502', "The airline's tracking site blocked the lookup (anti-bot). Retry shortly."],
  ['rate_limited', '502', 'Upstream rate limit hit. Back off and retry after a short pause.'],
  ['upstream_error', '502', 'The airline source errored or timed out. Safe to retry.'],
]

// AirLocation — a place (station / airport), aligned with the GDS Location schema so the front-end can
// plot it on a map. Every field optional; a connector that only knows the IATA code sets just iata_code.
// Reused verbatim by origin_location, destination_location, each event's location, and a route's from/to.
const AIR_LOCATION_FIELDS: Field[] = [
  { name: 'iata_code', type: 'string | null', desc: '3-letter IATA airport code.', example: 'MNL' },
  {
    name: 'icao_code',
    type: 'string | null',
    desc: '4-letter ICAO airport code.',
    example: 'RPLL',
  },
  {
    name: 'name',
    type: 'string | null',
    desc: 'Airport name.',
    example: 'Ninoy Aquino International Airport',
  },
  { name: 'city', type: 'string | null', desc: 'Nearest city.', example: 'Manila' },
  {
    name: 'state',
    type: 'string | null',
    desc: 'State / region, when known.',
    example: 'Central Visayas',
  },
  { name: 'country', type: 'string | null', desc: 'Country name.', example: 'Philippines' },
  {
    name: 'country_code',
    type: 'string | null',
    desc: 'ISO 3166-1 alpha-2 country code.',
    example: 'PH',
  },
  { name: 'lat', type: 'number | null', desc: 'Latitude, decimal degrees.', example: '14.5086' },
  { name: 'lng', type: 'number | null', desc: 'Longitude, decimal degrees.', example: '121.02' },
  {
    name: 'timezone',
    type: 'string | null',
    desc: 'IANA timezone of the station.',
    example: 'Asia/Manila',
  },
]

// AirDateTimes — an estimated/actual datetime pair (ISO 8601), the GDS datetime_local{estimated,actual}.
// estimated is the plan; actual is set once the milestone truly happens (actual != null ⇒ is_actual).
// Reused by shipment departure/arrival, each event's datetimes, and a route leg's departure/arrival.
const DATETIME_FIELDS: Field[] = [
  {
    name: 'estimated',
    type: 'string | null (ISO 8601)',
    desc: 'The planned/forecast time. UTC.',
    example: '2014-09-25T00:00:00Z',
  },
  {
    name: 'actual',
    type: 'string | null (ISO 8601)',
    desc: 'The realised time, set once the milestone actually happened. UTC.',
    example: '2014-09-25T03:25:00Z',
  },
  {
    name: 'datetime_precision',
    type: "'datetime' | 'date' | null",
    desc: "How precise actual ?? estimated is. 'date' when the airline gave a day with no hour: the value is that station's midnight and must not be read as a time of day. Null when both times are null.",
    example: 'date',
  },
]

const SCHEMA: Field[] = [
  {
    name: 'awb',
    type: 'string',
    desc: 'The canonical air waybill: 3-digit airline prefix + 8-digit serial (last digit is a mod-7 check).',
    example: '203-12345675',
  },
  {
    name: 'airline_prefix',
    type: 'string',
    desc: 'The 3-digit IATA numeric prefix at the head of the AWB, identifies the issuing airline.',
    example: '203',
  },
  {
    name: 'airline_iata',
    type: 'string',
    desc: 'The 2-character IATA airline code the prefix resolves to.',
    example: '5J',
  },
  {
    name: 'airline_name',
    type: 'string',
    desc: 'The issuing airline, display name.',
    example: 'Cebu Pacific Cargo',
  },
  {
    name: 'status',
    type: 'enum',
    desc: 'Normalised, cross-carrier lifecycle status, derived from the latest actual milestone.',
    example: 'delivered',
    enumDocs: STATUS_DOCS,
  },
  {
    name: 'origin',
    type: 'string | null',
    desc: 'Origin station, 3-letter IATA airport code.',
    example: 'CEB',
  },
  {
    name: 'origin_name',
    type: 'string | null',
    desc: 'Origin station name.',
    example: 'Ninoy Aquino International Airport',
  },
  {
    name: 'origin_location',
    type: 'AirLocation | null',
    desc: 'Richer origin geo (ICAO, city, country, lat/lng, timezone) for plotting the origin on a map. Fields:',
    children: AIR_LOCATION_FIELDS,
  },
  {
    name: 'destination',
    type: 'string | null',
    desc: 'Destination station, 3-letter IATA airport code.',
    example: 'MNL',
  },
  {
    name: 'destination_name',
    type: 'string | null',
    desc: 'Destination station name.',
    example: 'Mactan Cebu International Airport',
  },
  {
    name: 'destination_location',
    type: 'AirLocation | null',
    desc: 'Richer destination geo, same AirLocation shape as origin_location. Fields:',
    children: AIR_LOCATION_FIELDS,
  },
  {
    name: 'pieces',
    type: 'number | null',
    desc: 'Total number of pieces on the air waybill.',
    example: '1',
  },
  {
    name: 'weight_kg',
    type: 'number | null',
    desc: 'Total gross weight of the consignment, in kilograms.',
    example: '5',
  },
  {
    name: 'flight_number',
    type: 'string | null',
    desc: 'The current flight, IATA notation: the one the freight is on, else the next after the last flown leg; once every leg is flown, the last one.',
    example: '5J561',
  },
  {
    name: 'departure',
    type: 'AirDateTimes',
    desc: 'Shipment-level uplift at origin, as an estimated/actual pair. Fields:',
    children: DATETIME_FIELDS,
  },
  {
    name: 'arrival',
    type: 'AirDateTimes',
    desc: 'Shipment-level arrival at destination, as an estimated/actual pair. Fields:',
    children: DATETIME_FIELDS,
  },
  {
    name: 'eta',
    type: 'string | null (ISO 8601)',
    desc: 'Estimated (or actual, see eta_is_actual) arrival at the destination station, UTC. Resolved as arrival.actual ?? arrival.estimated.',
    example: '2014-09-25T04:35:00Z',
  },
  {
    name: 'eta_is_actual',
    type: 'boolean',
    desc: 'True when eta reflects an arrival that already happened (from an ARR/RCF actual), not a forecast.',
    example: 'true',
  },
  {
    name: 'eta_precision',
    type: "'datetime' | 'date' | null",
    desc: "'date' when the ETA is a day with no hour (that station's midnight); null when there is no ETA.",
    example: 'datetime',
  },
  {
    name: 'events',
    type: 'array<AirEvent>',
    desc: 'Chronological Cargo-IMP milestones, oldest first. Each entry:',
    children: [
      {
        name: 'status_code',
        type: 'enum (AirStatusCode)',
        desc: 'IATA Cargo-IMP status code. Unknown codes pass through as a raw string. What each means:',
        example: 'DEP',
        enumDocs: AIR_STATUS_CODES,
      },
      {
        name: 'event_family',
        type: 'enum',
        desc: 'The coarse phase this milestone belongs to.',
        example: 'FLIGHT',
        enumDocs: FAMILY_DOCS,
      },
      {
        name: 'is_actual',
        type: 'boolean',
        desc: 'True for a milestone that has happened; false for a planned/estimated one.',
        example: 'true',
      },
      {
        name: 'datetime',
        type: 'string (ISO 8601)',
        desc: 'Resolved time of the milestone (actual ?? estimated), UTC. Kept flat for sorting and back-compat.',
        example: '2014-09-25T03:25:00Z',
      },
      {
        name: 'datetimes',
        type: 'AirDateTimes',
        desc: 'The estimated + actual pair, when the source distinguishes them. Fields:',
        children: DATETIME_FIELDS,
      },
      {
        name: 'station',
        type: 'string | null',
        desc: 'Where it occurred, 3-letter IATA station code.',
        example: 'MNL',
      },
      {
        name: 'station_name',
        type: 'string | null',
        desc: 'Station name.',
        example: 'Ninoy Aquino International Airport',
      },
      {
        name: 'location',
        type: 'AirLocation | null',
        desc: 'Richer geo for this event (ICAO, city, country, lat/lng, timezone), same AirLocation shape. Fields:',
        children: AIR_LOCATION_FIELDS,
      },
      {
        name: 'flight_number',
        type: 'string | null',
        desc: 'The flight the piece moved on, IATA notation.',
        example: '5J561',
      },
      {
        name: 'pieces',
        type: 'number | null',
        desc: 'Pieces moved in this event (a split shipment can move in parts).',
        example: '1',
      },
      {
        name: 'weight_kg',
        type: 'number | null',
        desc: 'Gross weight moved in this event, kilograms.',
        example: '5',
      },
      {
        name: 'description',
        type: 'string | null',
        desc: 'Free-text note from the airline, when supplied.',
        example: 'Departed, 1 pc / 5 kg, flight: 5J561, to: CEB',
      },
    ],
  },
  {
    name: 'routes',
    type: 'array<AirRoute>',
    desc: 'Per-leg breakdown of the journey (one entry per flight or trucking segment), when the source provides it. Each leg carries a great-circle path polyline for drawing the route on a map. Each entry:',
    children: [
      {
        name: 'order_id',
        type: 'number | null',
        desc: 'Leg sequence number, 1-based, in journey order.',
        example: '1',
      },
      {
        name: 'status',
        type: 'string | null',
        desc: 'Upstream leg status (ARRIVED, IN_TRANSIT, …).',
        example: 'ARRIVED',
      },
      {
        name: 'transport_type',
        type: 'enum (PLANE | TRUCK)',
        desc: 'Mode of this leg. Road-feeder segments come through as TRUCK; unknown modes pass through as a raw string.',
        example: 'PLANE',
      },
      {
        name: 'from',
        type: 'AirLocation | null',
        desc: 'Leg origin, full AirLocation. Fields:',
        children: AIR_LOCATION_FIELDS,
      },
      {
        name: 'to',
        type: 'AirLocation | null',
        desc: 'Leg destination, full AirLocation. Fields:',
        children: AIR_LOCATION_FIELDS,
      },
      {
        name: 'departure',
        type: 'AirDateTimes',
        desc: 'Uplift on this leg, estimated/actual pair. Fields:',
        children: DATETIME_FIELDS,
      },
      {
        name: 'arrival',
        type: 'AirDateTimes',
        desc: 'Arrival on this leg, estimated/actual pair. Fields:',
        children: DATETIME_FIELDS,
      },
      {
        name: 'flight_number',
        type: 'string | null',
        desc: 'Flight number for this leg, IATA notation.',
        example: '5J561',
      },
      { name: 'pieces', type: 'number | null', desc: 'Pieces carried on this leg.', example: '1' },
      {
        name: 'weight_kg',
        type: 'number | null',
        desc: 'Gross weight carried on this leg, kilograms.',
        example: '5',
      },
      {
        name: 'path',
        type: 'Array<[number, number]> | null',
        desc: 'The great-circle polyline for the leg, an ordered list of [lat, lng] points from origin to destination. Drop it straight onto a map to draw the flown route.',
        example: '[[14.5086, 121.02], …, [10.3075, 123.979]]',
      },
    ],
  },
  {
    name: 'metadata',
    type: 'AwbMetadata',
    desc: 'Freshness metadata for the answer: the airline it resolved to, when it was last refreshed, and cache state. The raw upstream body is stripped at the API boundary and never returned. Fields:',
    children: [
      {
        name: 'status_code',
        type: 'string',
        desc: 'Upstream status_code (OK, AIRLINE_HASNT_PROVIDE_INFO, …).',
        example: 'OK',
      },
      {
        name: 'airline',
        type: 'object',
        desc: 'The airline the AWB resolved to, as reported by the source:',
        children: [
          { name: 'name', type: 'string | null', desc: 'Airline name.', example: 'Cebu Pacific' },
          { name: 'prefix', type: 'string | null', desc: '3-digit AWB prefix.', example: '203' },
          {
            name: 'iata_code',
            type: 'string | null',
            desc: '2-char IATA airline code.',
            example: '5J',
          },
          {
            name: 'icao_code',
            type: 'string | null',
            desc: '3-char ICAO airline code.',
            example: 'CEB',
          },
          {
            name: 'url',
            type: 'string | null',
            desc: "The airline's public tracking site.",
            example: 'https://www.cebupacificair.com',
          },
        ],
      },
      {
        name: 'updated_at',
        type: 'string | null (ISO 8601)',
        desc: 'When the tracking data was last refreshed ("data as of"), UTC.',
        example: '2026-07-30T20:18:12Z',
      },
      {
        name: 'from_cache',
        type: 'boolean',
        desc: 'True when this answer was served from our cache rather than a fresh fetch.',
        example: 'false',
      },
      {
        name: 'cache_expires',
        type: 'string | null (ISO 8601)',
        desc: 'When our cached copy goes stale and is re-fetched, UTC. Status-aware TTL: 61 min while in transit, 6 h once delivered.',
        example: '2026-07-30T21:19:12Z',
      },
    ],
  },
]

function SchemaRows({ fields, depth = 0 }: { fields: Field[]; depth?: number }) {
  return (
    <>
      {fields.map((f) => (
        <div
          key={f.name}
          className="dev-fld"
          style={
            depth > 0 ? { marginLeft: 18, borderLeft: '1px solid var(--dev-border)' } : undefined
          }
        >
          <div className="dev-fld-head">
            <code className="dev-fname">{f.name}</code>
            <span className="dev-ftype">{f.type}</span>
          </div>
          <div className="dev-fdesc">{f.desc}</div>
          {f.enumDocs && (
            <div className="dev-enumlist">
              {f.enumDocs.map(([code, meaning]) => (
                <div className="dev-enumrow" key={code}>
                  <code className="dev-enum">{code}</code>
                  <span className="dev-enummeaning">{meaning}</span>
                </div>
              ))}
            </div>
          )}
          {f.example && (
            <div className="dev-fex">
              Example: <code>{f.example}</code>
            </div>
          )}
          {f.children && <SchemaRows fields={f.children} depth={depth + 1} />}
        </div>
      ))}
    </>
  )
}

// Every snippet is a real, runnable call against the live API. Plain HTTP, no fictional SDK.
const SNIPPETS: Record<string, string> = {
  curl: `# no key needed on the demo AWB. the sandbox always answers.
curl https://api.aircargomcp.com/track/203-12345675`,
  ts: `// GET /track/:awb, any AWB format works ("203-1234 5675", "20312345675")
const res = await fetch(
  "https://api.aircargomcp.com/track/203-12345675"
);
const { data } = await res.json();
console.log(data.status, data.eta, data.events.at(-1)?.status_code);
// → "delivered" "2014-09-25T04:35:00Z" "DLV"`,
  py: `import requests

r = requests.get("https://api.aircargomcp.com/track/203-12345675")
data = r.json()["data"]
print(data["status"], data["eta"], data["events"][-1]["status_code"])
# → delivered 2014-09-25T04:35:00Z DLV`,
  mcp: `// Claude Desktop config  ·  or any mcp-remote client
{
  "mcpServers": {
    "air-cargo": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote", "https://mcp.aircargomcp.com/mcp",
        "--header", "Authorization: Bearer YOUR_API_KEY"
      ]
    }
  }
}`,
}

// Standalone example payloads, held as consts so the syntax highlighter and the copy button
// render from one source of truth (and never drift).
const PREFIX_REQ = 'curl https://api.aircargomcp.com/prefix/176'
const PREFIX_RES = `{
  "data": {
    "prefix": "176",
    "iata": "EK",
    "name": "Emirates SkyCargo",
    "site": "https://www.skycargo.com/...",
    "platform": null
  }
}`
const POSITION_RES = `{
  "data": [
    {
      "callsign": "UAE9812",
      "registration": "A6-EFG",
      "icao24_hex": "896420",
      "aircraft_type": "B77L",
      "lat": 25.31, "lon": 55.42,
      "alt_ft": 37000,
      "ground_speed_kt": 486,
      "heading": 291,
      "vertical_rate_fpm": 0,
      "operator_iata": "EK",
      "seen_at": "2026-07-30T09:12:04Z"
    }
  ]
}`
const RATE_RES = `// body: { origin, destination, weight_kg, volume_cm3? }
{
  "data": {
    "origin": "HKG",
    "destination": "FRA",
    "chargeable_weight_kg": 250,
    "applied_band": "+100",
    "net_per_kg": 3.8,
    "surcharge_per_kg": 1.1,
    "all_in_per_kg": 4.9,
    "total_charge": 1225,
    "currency": "USD",
    "source": "freightos-fax"
  }
}`

// Tiny JSON/code tokenizer → real React spans (no innerHTML). Same technique as trackingmcp.
type Seg = { t: string; c?: string }
function tokenize(src: string): Seg[] {
  const re =
    /(\/\/[^\n]*|#[^\n]*)|("(?:[^"\\]|\\.)*")(\s*:)?|\b(true|false|null)\b|(-?\d+\.?\d*)|([{}[\],:])/g
  const out: Seg[] = []
  let last = 0
  let m = re.exec(src)
  while (m) {
    if (m.index > last) out.push({ t: src.slice(last, m.index) })
    if (m[1]) out.push({ t: m[1], c: 'dev-cm' })
    else if (m[2]) {
      if (m[3] !== undefined) {
        out.push({ t: m[2], c: 'dev-k' })
        out.push({ t: m[3] })
      } else out.push({ t: m[2], c: 'dev-s' })
    } else if (m[4]) out.push({ t: m[4], c: 'dev-b' })
    else if (m[5]) out.push({ t: m[5], c: 'dev-n' })
    else if (m[6]) out.push({ t: m[6], c: 'dev-p' })
    last = re.lastIndex
    m = re.exec(src)
  }
  if (last < src.length) out.push({ t: src.slice(last) })
  return out
}
function Hl({ code }: { code: string }) {
  let off = 0
  return (
    <>
      {tokenize(code).map((s) => {
        const key = off
        off += s.t.length
        return s.c ? (
          <span key={key} className={s.c}>
            {s.t}
          </span>
        ) : (
          <span key={key}>{s.t}</span>
        )
      })}
    </>
  )
}
// A small copy affordance for any code pane. Local state, resets after a beat, never throws.
function Copy({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      className="dev-copy"
      onClick={() => {
        navigator.clipboard?.writeText(text).catch(() => {})
        setDone(true)
        setTimeout(() => setDone(false), 1400)
      }}
    >
      {done ? 'Copied' : label}
    </button>
  )
}

// Window-chrome header for a code pane: traffic dots, a title, an optional copy button.
function PaneHead({ title, code }: { title: string; code?: string }) {
  return (
    <div className="dev-hl">
      <span className="dev-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <span className="dev-hl-t">{title}</span>
      {code && <Copy text={code} label="Copy" />}
    </div>
  )
}

// ── The four layer micro-visuals ────────────────────────────────────────────────────────────────
// Each is subject-true, drawn as crisp inline SVG on a 240×46 canvas, themed off the -viz CSS classes
// so it re-resolves in the light + dark token grounds. No shared sparkline: every card shows its own
// instrument.

// Layer 01 — the IATA Cargo-IMP milestone chip-timeline. Origin acceptance through the air leg, with the
// current milestone pinged and the tail still dashed/pending, so it reads as a live shipment in motion.
function VizTrack() {
  const stops = ['RCS', 'DEP', 'ARR', 'RCF', 'DLV']
  const x0 = 16
  const x1 = 224
  const y = 20
  const at = (i: number) => x0 + (i / (stops.length - 1)) * (x1 - x0)
  const cur = 3 // RCS·DEP·ARR done, RCF is the live tip, DLV still pending
  return (
    <svg
      className="dev-viz"
      viewBox="0 0 240 46"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Cargo-IMP milestone timeline"
    >
      <line
        className="viz-track"
        x1={x0}
        y1={y}
        x2={at(cur)}
        y2={y}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <line
        className="viz-dim"
        x1={at(cur)}
        y1={y}
        x2={x1}
        y2={y}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeDasharray="1.5 3.5"
      />
      {stops.map((s, i) => (
        <g key={s}>
          {i < cur && <circle className="viz-node" cx={at(i)} cy={y} r="3.4" />}
          {i === cur && (
            <>
              <circle className="viz-ping" cx={at(i)} cy={y} r="3.6" />
              <circle className="viz-node" cx={at(i)} cy={y} r="3.6" />
            </>
          )}
          {i > cur && <circle className="viz-node-o" cx={at(i)} cy={y} r="3" strokeWidth="1.4" />}
          <text className="viz-label" x={at(i)} y="37" textAnchor="middle">
            {s}
          </text>
        </g>
      ))}
    </svg>
  )
}

// Layer 02 — the prefix→airline resolve. The 3-digit AWB prefix chip on the left resolves through the
// oracle to the issuing airline chip on the right, IATA badge plus name.
function VizPrefix() {
  return (
    <svg
      className="dev-viz"
      viewBox="0 0 240 46"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="AWB prefix resolves to airline"
    >
      <text className="viz-label" x="27" y="9" textAnchor="middle">
        PREFIX
      </text>
      <rect className="viz-chip" x="6" y="13" width="42" height="22" rx="6" />
      <text className="viz-fig viz-mono" x="27" y="28" textAnchor="middle" fontSize="12">
        203
      </text>
      <line className="viz-track" x1="54" y1="24" x2="70" y2="24" strokeWidth="1.5" />
      <path className="viz-sig" d="M76 24 l-6 -3.2 v6.4 z" />
      <text className="viz-label" x="86" y="9">
        AIRLINE
      </text>
      <rect className="viz-chip-sig" x="82" y="13" width="152" height="22" rx="6" />
      <text className="viz-sig viz-mono" x="92" y="28" fontSize="11">
        5J
      </text>
      <text className="viz-ink" x="112" y="28" fontSize="10.5">
        Cebu Pacific
      </text>
    </svg>
  )
}

// Layer 03 — the freighter-position instrument. A great-circle arc drawn origin→destination with a plane
// glyph riding the tangent at cruise and a flight-level readout, the "where is the plane" layer.
function VizPosition() {
  return (
    <svg
      className="dev-viz"
      viewBox="0 0 240 46"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Great-circle route with live aircraft position"
    >
      <path
        className="viz-arc"
        d="M18 34 Q120 -2 222 18"
        pathLength={100}
        strokeWidth="1.6"
        fill="none"
      />
      <circle className="viz-node" cx="18" cy="34" r="3" />
      <circle className="viz-node-o" cx="222" cy="18" r="3" strokeWidth="1.4" />
      <text className="viz-label" x="12" y="44">
        MNL
      </text>
      <text className="viz-label" x="228" y="13" textAnchor="end">
        CEB
      </text>
      <g transform="translate(130.2 11.3) rotate(-2.9)">
        <path className="viz-sig" d="M7 0 L-5 -3.4 L-1.6 0 L-5 3.4 Z" />
      </g>
      <text className="viz-fig viz-mono" x="122" y="42" textAnchor="middle" fontSize="7.5">
        FL370 · 486kt
      </text>
    </svg>
  )
}

// Layer 04 — the per-kg weight-break rate curve. A stepped descent as the shipment crosses the
// +45 / +100 / +300 / +1000 kg bands and the per-kilo rate falls, with the band it lands in marked.
function VizRate() {
  return (
    <svg
      className="dev-viz"
      viewBox="0 0 240 46"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Per-kg rate stepping down across weight breaks"
    >
      <line className="viz-dim" x1="14" y1="40" x2="226" y2="40" strokeWidth="1" />
      <path className="viz-area" d="M14 11 H56 V18 H98 V24 H140 V29 H182 V33 H226 V40 H14 Z" />
      <path
        className="viz-step"
        d="M14 11 H56 V18 H98 V24 H140 V29 H182 V33 H226"
        strokeWidth="1.6"
        fill="none"
      />
      <circle className="viz-node" cx="14" cy="11" r="1.9" />
      <circle className="viz-node" cx="56" cy="18" r="1.9" />
      <circle className="viz-node" cx="98" cy="24" r="1.9" />
      <circle className="viz-node" cx="140" cy="29" r="1.9" />
      <circle className="viz-node" cx="182" cy="33" r="1.9" />
      <text className="viz-label" x="14" y="8">
        $/kg
      </text>
      <text className="viz-label" x="140" y="29" dy="-4" textAnchor="middle">
        +100
      </text>
    </svg>
  )
}

const VIZ: Record<string, () => JSX.Element> = {
  track: VizTrack,
  prefix: VizPrefix,
  position: VizPosition,
  rate: VizRate,
}

const CSS = `
.dev{
  --dev-sans:'Switzer',ui-sans-serif,system-ui,-apple-system,sans-serif;
  --dev-disp:'Ranade','Switzer',Georgia,serif;
  --dev-mono:'JetBrains Mono',ui-monospace,'SF Mono',Menlo,monospace;
  font-family:var(--dev-sans);
}
/* Token aliases derived from the navo24 -rgb tokens. A var() inside a custom-property value is resolved
   at the DECLARING element, then inherits, so the alias set must be RE-declared inside the .tone-dark
   island for it to re-resolve against the dark tokens. Two declarations, one contract, both grounds. */
.dev,
.dev .tone-dark,
.dev .tone-dark-vars{
  --dev-ink:rgb(var(--ink-rgb));
  --dev-2:rgb(var(--muted-rgb));
  --dev-muted:rgb(var(--muted-rgb) / .82);
  --dev-faint:rgb(var(--faint-rgb));
  --dev-border:rgb(var(--border-rgb) / .1);
  --dev-border-strong:rgb(var(--border-rgb) / .18);
  --dev-s0:rgb(var(--surface-0-rgb));
  --dev-s1:rgb(var(--surface-1-rgb));
  --dev-s2:rgb(var(--surface-2-rgb));
  --dev-signal:rgb(var(--signal-rgb));
  --dev-on:rgb(var(--on-accent-rgb));
  --dev-danger:rgb(var(--down-rgb));
}
.dev{ --dev-warn:#b26a12; --dev-tk-k:#1f4fe6; --dev-tk-s:#0a8f66; --dev-tk-n:#b45612; --dev-tk-b:#7c3aed; }
.dev .tone-dark, .dev .tone-dark-vars{
  --dev-warn:#f5b942;
  --dev-tk-k:#7aa2ff; --dev-tk-s:#57d7b6; --dev-tk-n:#fbbf24; --dev-tk-b:#c49bff;
}
.dev-wrap{ max-width:1180px; margin:0 auto; padding:0 28px; }
.dev-mono{ font-family:var(--dev-mono); }
.dev h1,.dev h2,.dev h3{ font-family:var(--dev-disp); font-weight:340; }

.dev-hero{ display:grid; grid-template-columns:1fr 1.05fr; gap:44px; align-items:center; padding:56px 0 52px; }
.dev-eyebrow{ font-family:var(--dev-mono); font-size:12px; letter-spacing:.16em; text-transform:uppercase;
  color:var(--dev-signal); display:flex; align-items:center; gap:10px; }
.dev-eyebrow::before{ content:""; width:22px; height:1px; background:var(--dev-signal); opacity:.6; }
.dev h1{ font-size:clamp(33px,4.4vw,52px); line-height:1.03; letter-spacing:-.03em; margin:16px 0 0; text-wrap:balance; color:var(--dev-ink); }
.dev-sub{ color:var(--dev-2); font-size:16px; margin-top:16px; max-width:48ch; line-height:1.55; }
.dev-ctarow{ display:flex; gap:12px; margin-top:24px; flex-wrap:wrap; }
.dev-btn{ display:inline-flex; align-items:center; gap:8px; height:38px; padding:0 17px; border-radius:9px;
  font-size:13.5px; font-weight:600; border:1px solid transparent; transition:filter .15s,background .15s,border-color .15s; cursor:pointer; }
.dev-btn.solid{ background:var(--dev-signal); color:var(--dev-on); }
.dev-btn.solid:hover{ filter:brightness(1.06); }
.dev-btn.ghost{ border-color:var(--dev-border-strong); color:var(--dev-ink); }
.dev-btn.ghost:hover{ background:var(--dev-s1); }
.dev-btn.mono{ font-family:var(--dev-mono); font-size:12.5px; }
.dev-trust{ display:flex; flex-wrap:wrap; gap:8px 10px; margin-top:24px; font-family:var(--dev-mono); font-size:12px; color:var(--dev-2); }
.dev-trust span{ border:1px solid var(--dev-border); border-radius:999px; padding:5px 11px; display:inline-flex; gap:7px; align-items:center; }
.dev-trust b{ color:var(--dev-ink); font-weight:600; }
.dev-trust em{ color:var(--dev-signal); font-style:normal; }

.dev-term{ background:var(--dev-s0); border:1px solid var(--dev-border-strong); border-radius:14px; overflow:hidden;
  box-shadow:var(--shadow); }
.dev-tbar{ display:flex; align-items:center; gap:10px; padding:11px 14px; border-bottom:1px solid var(--dev-border); background:var(--dev-s1); }
.dev-tbar .tag{ font-family:var(--dev-mono); font-size:11.5px; color:var(--dev-faint); letter-spacing:.04em; }
.dev-tbar .tag b{ color:var(--dev-2); font-weight:600; }
.dev-tbar .live{ margin-left:auto; font-family:var(--dev-mono); font-size:11px; color:var(--dev-signal); display:inline-flex; align-items:center; gap:6px; }
.dev-pip{ width:6px; height:6px; border-radius:50%; background:var(--dev-signal); box-shadow:0 0 7px var(--dev-signal); animation:dev-breathe 2.6s ease-in-out infinite; }
@keyframes dev-breathe{ 0%,100%{opacity:.5} 50%{opacity:1} }
.dev-req{ display:flex; align-items:center; gap:10px; padding:12px 14px; border-bottom:1px solid var(--dev-border); }
.dev-meth{ font-family:var(--dev-mono); font-size:11px; font-weight:700; letter-spacing:.05em; padding:3px 8px; border-radius:6px;
  color:var(--dev-signal); border:1px solid color-mix(in srgb, var(--dev-signal) 35%, transparent); background:color-mix(in srgb, var(--dev-signal) 8%, transparent); }
.dev-meth.post{ color:var(--dev-warn); border-color:color-mix(in srgb, var(--dev-warn) 35%, transparent); background:color-mix(in srgb, var(--dev-warn) 10%, transparent); }
.dev-url{ font-family:var(--dev-mono); font-size:12.5px; color:var(--dev-2); flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.dev-run{ height:30px; padding:0 16px; border-radius:8px; background:var(--dev-signal); color:var(--dev-on); font-weight:700; font-size:12.5px;
  font-family:var(--dev-mono); display:inline-flex; align-items:center; gap:7px; border:0; cursor:pointer; }
.dev-run:hover{ filter:brightness(1.06); } .dev-run[disabled]{ opacity:.6; cursor:default; }
.dev-spin{ width:11px; height:11px; border:2px solid color-mix(in srgb, var(--dev-on) 40%, transparent); border-top-color:var(--dev-on); border-radius:50%; animation:dev-spin .6s linear infinite; }
@keyframes dev-spin{ to{ transform:rotate(360deg) } }
.dev-respbar{ display:flex; align-items:center; gap:14px; padding:8px 14px; border-bottom:1px solid var(--dev-border);
  font-family:var(--dev-mono); font-size:11.5px; color:var(--dev-faint); background:var(--dev-s1); }
.dev-respbar .ok{ color:var(--dev-signal); }
.dev-respbar .srcs{ margin-left:auto; display:flex; gap:6px; }
.dev-respbar .srcs i{ width:6px; height:6px; border-radius:50%; background:var(--dev-signal); box-shadow:0 0 6px color-mix(in srgb,var(--dev-signal) 60%,transparent); }
.dev-code{ margin:0; padding:16px; font-family:var(--dev-mono); font-size:12.5px; line-height:1.65; color:var(--dev-ink);
  overflow:auto; max-height:400px; white-space:pre; tab-size:2; }
.dev-k{color:var(--dev-tk-k)} .dev-s{color:var(--dev-tk-s)} .dev-n{color:var(--dev-tk-n)} .dev-b{color:var(--dev-tk-b)} .dev-p{color:var(--dev-faint)} .dev-cm{color:var(--dev-faint);font-style:italic}

.dev-sec{ padding:48px 0; }
.dev-sech{ display:flex; align-items:baseline; gap:14px; margin-bottom:22px; }
.dev-sech .num{ font-family:var(--dev-mono); font-size:12px; color:var(--dev-signal); }
.dev-sech h2{ font-size:24px; letter-spacing:-.02em; color:var(--dev-ink); }
.dev-sech p{ color:var(--dev-faint); font-size:14px; margin-left:auto; max-width:40ch; text-align:right; }
.dev-cap{ margin-top:16px; color:var(--dev-muted); font-size:13px; line-height:1.7; max-width:80ch; }
.dev-cap code{ font-family:var(--dev-mono); font-size:11.5px; color:var(--dev-2); background:var(--dev-s1);
  border:1px solid var(--dev-border); border-radius:5px; padding:1px 6px; white-space:nowrap; }

.dev-console{ border:1px solid var(--dev-border); border-radius:12px; overflow:hidden; background:var(--dev-s0); }
.dev-row{ display:grid; grid-template-columns:66px 1fr auto; gap:14px; align-items:center; padding:12px 16px; border-bottom:1px solid var(--dev-border); font-family:var(--dev-mono); font-size:13px; }
.dev-row:last-child{ border-bottom:0; } .dev-row:hover{ background:var(--dev-s1); }
.dev-m{ font-size:10.5px; font-weight:700; letter-spacing:.05em; padding:3px 0; text-align:center; border-radius:5px;
  color:var(--dev-signal); border:1px solid color-mix(in srgb,var(--dev-signal) 30%,transparent); background:color-mix(in srgb,var(--dev-signal) 6%,transparent); }
.dev-m.post{ color:var(--dev-warn); border-color:color-mix(in srgb,var(--dev-warn) 30%,transparent); background:color-mix(in srgb,var(--dev-warn) 8%,transparent); }
.dev-row .path{ color:var(--dev-ink); } .dev-row .desc{ color:var(--dev-faint); font-family:var(--dev-sans); font-size:13px; }

.dev-layers{ display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
.dev-ch{ border:1px solid var(--dev-border); border-radius:12px; padding:18px; background:var(--dev-s0); position:relative; overflow:hidden;
  display:flex; flex-direction:column; transition:transform .22s ease, border-color .22s ease, box-shadow .22s ease; }
.dev-ch::after{ content:""; position:absolute; left:0; right:0; bottom:0; height:2px; background:var(--dev-signal); opacity:.4; transition:opacity .22s ease; }
.dev-ch:hover{ transform:translateY(-3px); border-color:var(--dev-border-strong); box-shadow:0 18px 40px -30px rgba(0,0,0,.5); }
.dev-ch:hover::after{ opacity:.85; }
.dev-ch .lbl{ font-family:var(--dev-mono); font-size:11px; letter-spacing:.08em; text-transform:uppercase; color:var(--dev-faint); }
.dev-ch .nm{ font-weight:600; font-size:15px; margin-top:8px; display:flex; align-items:center; gap:8px; color:var(--dev-ink); }
.dev-ch p{ color:var(--dev-muted); font-size:13px; margin-top:8px; line-height:1.5; }
.dev-ch code{ font-family:var(--dev-mono); font-size:11px; color:var(--dev-2); }

/* Layer micro-visuals — a distinct instrument per card, themed off the token grounds. */
.dev-viz{ margin-top:auto; padding-top:16px; width:100%; height:auto; display:block; overflow:visible; }
.dev-viz text{ font-family:var(--dev-mono); }
.viz-track{ stroke:var(--dev-signal); }
.viz-dim{ stroke:var(--dev-border-strong); }
.viz-node{ fill:var(--dev-signal); }
.viz-node-o{ fill:var(--dev-s0); stroke:var(--dev-signal); }
.viz-sig{ fill:var(--dev-signal); }
.viz-ink{ fill:var(--dev-ink); }
.viz-mono{ font-weight:600; }
.viz-fig{ fill:var(--dev-ink); }
.viz-label{ fill:var(--dev-faint); font-size:6.5px; letter-spacing:.12em; }
.viz-chip{ fill:var(--dev-s1); stroke:var(--dev-border-strong); stroke-width:1; }
.viz-chip-sig{ fill:color-mix(in srgb,var(--dev-signal) 8%,transparent); stroke:color-mix(in srgb,var(--dev-signal) 32%,transparent); stroke-width:1; }
.viz-arc{ stroke:var(--dev-signal); stroke-linecap:round; stroke-dasharray:100; stroke-dashoffset:100; animation:dev-draw 1.5s ease .1s forwards; }
.viz-step{ stroke:var(--dev-signal); stroke-linejoin:round; }
.viz-area{ fill:color-mix(in srgb,var(--dev-signal) 9%,transparent); }
.viz-ping{ fill:var(--dev-signal); transform-box:fill-box; transform-origin:center; animation:dev-ping 2.4s ease-out infinite; }
@keyframes dev-draw{ to{ stroke-dashoffset:0; } }
@keyframes dev-ping{ 0%{ transform:scale(1); opacity:.5; } 70%,100%{ transform:scale(2.6); opacity:0; } }

.dev-split{ display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.dev-tabs{ display:flex; gap:2px; padding:6px; background:var(--dev-s1); border:1px solid var(--dev-border); border-radius:10px 10px 0 0; border-bottom:0; }
.dev-tabs button{ font-family:var(--dev-mono); font-size:12px; color:var(--dev-faint); padding:6px 12px; border-radius:7px; border:0; background:none; cursor:pointer; }
.dev-tabs button.on{ background:var(--dev-s2); color:var(--dev-ink); }
.dev-pbox{ border:1px solid var(--dev-border); border-radius:0 0 12px 12px; background:var(--dev-s0); overflow:hidden; }
.dev-hl{ display:flex; align-items:center; gap:10px; font-family:var(--dev-mono); font-size:11.5px; color:var(--dev-faint);
  padding:10px 12px 10px 14px; border-bottom:1px solid var(--dev-border); background:var(--dev-s1); letter-spacing:.03em; }
.dev-hl-t{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.dev-dots{ display:inline-flex; gap:5px; flex:0 0 auto; }
.dev-dots i{ width:8px; height:8px; border-radius:50%; background:var(--dev-border-strong); }
.dev-dots i:first-child{ background:color-mix(in srgb,var(--dev-danger) 60%,var(--dev-border-strong)); }
.dev-dots i:nth-child(2){ background:color-mix(in srgb,var(--dev-warn) 60%,var(--dev-border-strong)); }
.dev-dots i:nth-child(3){ background:color-mix(in srgb,var(--dev-signal) 55%,var(--dev-border-strong)); }
.dev-copy{ margin-left:auto; flex:0 0 auto; font-family:var(--dev-mono); font-size:11px; color:var(--dev-faint);
  border:1px solid var(--dev-border-strong); border-radius:7px; padding:4px 10px; background:none; cursor:pointer; transition:color .15s,border-color .15s; }
.dev-copy:hover{ color:var(--dev-ink); border-color:var(--dev-signal); }

/* Hero: the Cargo-IMP milestone ribbon, the characteristic air-cargo moment. */
.dev-ribbon{ display:flex; align-items:center; gap:6px; padding:13px 16px; border-bottom:1px solid var(--dev-border); background:var(--dev-s1); overflow-x:auto; }
.dev-mile{ display:flex; align-items:center; gap:9px; flex:0 0 auto; }
.dev-mile .dot{ width:8px; height:8px; border-radius:50%; background:var(--dev-signal); box-shadow:0 0 8px color-mix(in srgb,var(--dev-signal) 60%,transparent); flex:0 0 auto; }
.dev-mile .mc{ font-family:var(--dev-mono); font-size:12px; font-weight:700; letter-spacing:.04em; color:var(--dev-ink); }
.dev-mile .mt{ font-family:var(--dev-mono); font-size:11px; color:var(--dev-faint); font-variant-numeric:tabular-nums; }
.dev-ribbon .seg{ width:16px; height:1px; background:var(--dev-border-strong); flex:0 0 auto; }
.dev-ribbon .seg.dash{ background:repeating-linear-gradient(90deg,var(--dev-border-strong) 0 3px,transparent 3px 6px); }

.dev-mcpline{ display:flex; align-items:center; gap:12px; background:var(--dev-s0); border:1px solid var(--dev-border-strong); border-radius:12px; padding:14px 16px; font-family:var(--dev-mono); font-size:13px; }
.dev-mcpline .pr{ color:var(--dev-faint); } .dev-mcpline .cmd{ color:var(--dev-ink); flex:1; overflow:auto; white-space:nowrap; }
.dev-mcpline .cp{ color:var(--dev-faint); border:1px solid var(--dev-border-strong); border-radius:7px; padding:5px 11px; font-size:11.5px; background:none; cursor:pointer; }
.dev-mcpline .cp:hover{ color:var(--dev-ink); }
.dev-tools{ display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-top:16px; }
.dev-tool{ border:1px solid var(--dev-border); border-radius:10px; padding:13px 15px; background:var(--dev-s0); }
.dev-tool .t{ font-family:var(--dev-mono); font-size:13px; color:var(--dev-signal); } .dev-tool .d{ color:var(--dev-muted); font-size:12.5px; margin-top:5px; }

.dev-paramtable{ border:1px solid var(--dev-border); border-radius:12px; overflow:hidden; background:var(--dev-s0); }
.dev-paramrow{ display:grid; grid-template-columns:150px 130px 1fr; gap:14px; padding:12px 16px; border-bottom:1px solid var(--dev-border); font-size:13px; }
.dev-paramrow:last-child{ border-bottom:0; }
.dev-paramrow .pn{ font-family:var(--dev-mono); color:var(--dev-ink); font-weight:600; }
.dev-paramrow .pt{ font-family:var(--dev-mono); color:var(--dev-faint); font-size:11.5px; }
.dev-paramrow .pd{ color:var(--dev-muted); font-family:var(--dev-sans); line-height:1.5; }
.dev-paramrow .req{ color:var(--dev-danger); font-size:10px; font-family:var(--dev-mono); }

.dev-cta{ border:1px solid var(--dev-border-strong); border-radius:16px; padding:44px; text-align:center; background:var(--dev-s0); }
.dev-cta h2{ font-size:30px; letter-spacing:-.02em; margin-bottom:10px; color:var(--dev-ink); }
.dev-cta p{ color:var(--dev-2); margin-bottom:22px; }
.dev-cta .r{ display:flex; gap:12px; justify-content:center; flex-wrap:wrap; }
.dev-schema{ border:1px solid var(--dev-border); border-radius:12px; overflow:hidden; background:var(--dev-s0); }
.dev-fld{ padding:13px 16px; border-bottom:1px solid var(--dev-border); }
.dev-fld:last-child{ border-bottom:0; }
.dev-fld-head{ display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; }
.dev-fname{ font-family:var(--dev-mono); font-size:13px; font-weight:600; color:var(--dev-ink); }
.dev-ftype{ font-family:var(--dev-mono); font-size:11.5px; color:var(--dev-faint); }
.dev-fdesc{ font-family:var(--dev-sans); font-size:13px; color:var(--dev-2); margin-top:5px; max-width:76ch; line-height:1.55; }
.dev-enum{ font-family:var(--dev-mono); font-size:11px; color:var(--dev-2); background:var(--dev-s1); border:1px solid var(--dev-border); border-radius:5px; padding:2px 7px; }
.dev-enumlist{ display:flex; flex-direction:column; gap:7px; margin-top:9px; }
.dev-enumrow{ display:flex; align-items:baseline; gap:9px; }
.dev-enumrow .dev-enum{ flex:0 0 auto; min-width:52px; text-align:center; }
.dev-enummeaning{ font-family:var(--dev-sans); font-size:12.5px; color:var(--dev-2); line-height:1.5; }
.dev-fex{ font-family:var(--dev-sans); font-size:12px; color:var(--dev-faint); margin-top:7px; }
.dev-fex code{ font-family:var(--dev-mono); color:var(--dev-2); background:var(--dev-s1); border-radius:4px; padding:1px 5px; }
.dev-slabel{ font-family:var(--dev-mono); font-size:12px; letter-spacing:.12em; text-transform:uppercase; color:var(--dev-faint); margin:26px 0 10px; }

/* Supported airlines / coverage */
.dev-statrow{ display:flex; flex-wrap:wrap; gap:10px; margin:0 0 20px; }
.dev-stat{ border:1px solid var(--dev-border); border-radius:11px; padding:13px 18px; background:var(--dev-s0); min-width:150px; }
.dev-stat b{ font-family:var(--dev-disp); font-weight:340; font-size:26px; line-height:1; color:var(--dev-ink); display:block; }
.dev-stat span{ font-family:var(--dev-mono); font-size:11px; letter-spacing:.03em; color:var(--dev-faint); margin-top:7px; display:block; }
.dev-airtable{ border:1px solid var(--dev-border); border-radius:12px; overflow:hidden; background:var(--dev-s0); }
.dev-airhead,.dev-airrow{ display:grid; grid-template-columns:1fr 56px 74px; gap:14px; align-items:center; padding:11px 16px; }
.dev-airhead{ background:var(--dev-s1); border-bottom:1px solid var(--dev-border); font-family:var(--dev-mono); font-size:10.5px; letter-spacing:.07em; text-transform:uppercase; color:var(--dev-faint); }
.dev-airrow{ border-bottom:1px solid var(--dev-border); font-size:13px; }
.dev-airrow:last-child{ border-bottom:0; } .dev-airrow:hover{ background:var(--dev-s1); }
.dev-airrow .an{ font-family:var(--dev-sans); color:var(--dev-ink); }
.dev-airrow .ai,.dev-airrow .ap{ font-family:var(--dev-mono); font-size:12px; color:var(--dev-2); }

/* Tabular figures everywhere digits should align. */
.dev-mono,.dev-code,.dev-stat b,.dev-trust,.dev-respbar,.dev-airrow .ai,.dev-airrow .ap,.dev-paramrow .pt,.dev-viz text{ font-variant-numeric:tabular-nums; }
@media (prefers-reduced-motion:reduce){
  .dev *{ animation:none !important; }
  .viz-arc{ stroke-dashoffset:0 !important; }
  .dev-ch:hover{ transform:none; }
}
@media (max-width:900px){
  .dev-hero{ grid-template-columns:1fr; gap:28px; padding:36px 0 20px; }
  .dev-layers{ grid-template-columns:1fr 1fr; } .dev-tools{ grid-template-columns:1fr 1fr; } .dev-split{ grid-template-columns:1fr; }
  .dev-sech{ flex-direction:column; } .dev-sech p{ text-align:left; margin-left:0; }
  .dev-paramrow{ grid-template-columns:1fr; gap:4px; }
}
@media (max-width:560px){
  .dev-wrap{ padding:0 16px; } .dev-layers,.dev-tools{ grid-template-columns:1fr; }
  .dev-airhead,.dev-airrow{ grid-template-columns:1fr 40px 92px; gap:10px; padding:10px 12px; }
}
`

function TryIt() {
  const [body, setBody] = useState(DEMO_SAMPLE)
  const [meta, setMeta] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const run = async () => {
    setLoading(true)
    const t0 = performance.now()
    try {
      const res = await fetch(`${API}/track/203-12345675`, {
        headers: { Accept: 'application/json' },
      })
      const json = await res.json()
      setBody(JSON.stringify(json, null, 2))
      setMeta(
        `${res.status} ${res.ok ? 'OK' : 'ERR'} · ${Math.max(1, Math.round(performance.now() - t0))} ms · live`,
      )
    } catch {
      setBody(DEMO_SAMPLE)
      setMeta('offline · showing sample')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="dev-term">
      <div className="dev-tbar">
        <span className="dev-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span className="tag">
          awb.<b>trace</b>
        </span>
        <span className="live">
          <span className="dev-pip" />
          live sandbox
        </span>
      </div>
      <div className="dev-req">
        <span className="dev-meth">GET</span>
        <span className="dev-url">api.aircargomcp.com/track/203-12345675</span>
        <button type="button" className="dev-run" onClick={run} disabled={loading}>
          {loading ? <span className="dev-spin" /> : 'Run'}
        </button>
      </div>
      <div className="dev-ribbon" aria-label="Cargo-IMP milestones for the demo AWB">
        {[
          ['RCS', '00:18'],
          ['DEP', '03:25'],
          ['DLV', '13:24'],
        ].map(([code, time], i) => (
          <span className="dev-mile" key={code}>
            {i > 0 && <span className="seg" />}
            <span className="dot" />
            <span className="mc">{code}</span>
            <span className="mt">{time}</span>
          </span>
        ))}
      </div>
      {meta && (
        <div className="dev-respbar">
          <span className="ok">{meta}</span>
          <span>203-12345675</span>
          <span className="srcs">
            <i />
            <i />
            <i />
          </span>
        </div>
      )}
      <pre className="dev-code">
        <Hl code={body} />
      </pre>
    </div>
  )
}

function CodeTabs() {
  const [lang, setLang] = useState<keyof typeof SNIPPETS>('curl')
  const tabs: [keyof typeof SNIPPETS, string][] = [
    ['curl', 'curl'],
    ['ts', 'Node'],
    ['py', 'Python'],
    ['mcp', 'MCP'],
  ]
  return (
    <div className="dev-split">
      <div>
        <div className="dev-tabs">
          {tabs.map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={lang === k ? 'on' : ''}
              onClick={() => setLang(k)}
            >
              {label}
            </button>
          ))}
          <Copy text={SNIPPETS[lang] ?? ''} />
        </div>
        <div className="dev-pbox">
          <pre className="dev-code">
            <Hl code={SNIPPETS[lang] ?? ''} />
          </pre>
        </div>
      </div>
      <div className="dev-term" style={{ alignSelf: 'start' }}>
        <PaneHead
          title="{ data: AwbTrackingData }, the delivered Cebu Pacific AWB"
          code={DEMO_SAMPLE}
        />
        <pre className="dev-code" style={{ maxHeight: 'none' }}>
          <Hl code={DEMO_SAMPLE} />
        </pre>
      </div>
    </div>
  )
}

const ENDPOINTS: [string, string, string][] = [
  [
    'GET',
    '/track/:awb',
    'The primary endpoint. Any AWB → status, route, pieces, weight, ETA, milestones.',
  ],
  [
    'GET',
    '/prefix/:code',
    '3-digit AWB prefix → the issuing airline (the AWB-prefix oracle). Free, offline.',
  ],
  [
    'POST',
    '/rates/quote',
    'Price a lane per kg: chargeable weight, weight band, surcharges, all-in.',
  ],
  [
    'GET',
    '/aircraft/flight/:flightNumber',
    'Live freighter position by flight number, the "where is the plane" layer.',
  ],
  ['GET', '/aircraft/reg/:tail', 'Live position by aircraft registration / tail number.'],
  ['GET', '/aircraft/callsign/:cs', 'Live position by raw ATC callsign.'],
]

const LAYERS: [string, string, string, string][] = [
  [
    'Layer 01',
    'AWB track & trace',
    'Every airline connector normalised onto one closed set of IATA Cargo-IMP milestones.',
    'track',
  ],
  [
    'Layer 02',
    'AWB-prefix oracle',
    'The 3-digit prefix → airline, offline and free. 83 cargo airlines mapped.',
    'prefix',
  ],
  [
    'Layer 03',
    'Freighter position',
    'Live ADS-B lat/lon/altitude for the aircraft carrying the leg, right now.',
    'position',
  ],
  [
    'Layer 04',
    'Rate quote',
    'All-in per-kg pricing on real Freightos FAX region rates and carrier cards.',
    'rate',
  ],
]

const TOOLS: [string, string][] = [
  ['track_awb', 'Track a shipment by AWB. Any format in, normalised timeline out.'],
  ['resolve_awb_prefix', 'Resolve a 3-digit AWB prefix to its issuing airline.'],
  ['air_rate_quote', 'All-in per-kg quote for a lane, with chargeable weight and surcharges.'],
]

// ── Supported airlines ─────────────────────────────────────────────────────────────────────────────
// The roster the API publishes: apps/api/src/data/air-coverage.generated.json (bun run coverage:build) is
// derived from the router itself, so this page cannot list a line /track refuses. ROSTER is [name, iata, prefix].
const ROSTER: [string, string, string][] = coverage.airlines.map((a): [string, string, string] => [
  a.name,
  a.iata,
  a.prefix,
])

const MCP_JSON = SNIPPETS.mcp

export default function DevelopersPage() {
  const [copied, setCopied] = useState(false)
  const copyMcp = () => {
    navigator.clipboard?.writeText(MCP_JSON).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <Layout onDark>
      <PageMeta
        title="Air Cargo Tracking API & MCP for AI agents | AirCargoMCP"
        description="Track any air waybill across the world's cargo airlines as one API call. Live IATA Cargo-IMP milestones, the AWB-prefix oracle, freighter position and per-kg rate quotes, over REST and MCP for AI agents."
        path="/developers"
      />
      <style>{CSS}</style>
      <div className="dev">
        {/* Hero: the API answering is the thesis, rides the deep-ink island. */}
        <div className="tone-dark overflow-x-clip">
          <section className="dev-wrap dev-hero">
            <div>
              <div className="dev-eyebrow">Developer platform</div>
              <h1>Air freight, as one API call.</h1>
              <p className="dev-sub">
                Track any air waybill across the world's cargo airlines. Every airline is normalised
                onto the same IATA Cargo-IMP milestones, so an Emirates AWB and a Cebu Pacific AWB
                read identically, over one REST endpoint and one MCP connection.
              </p>
              <div className="dev-ctarow">
                <a className="dev-btn solid" href="#track">
                  Read the docs
                </a>
                <a className="dev-btn ghost mono" href="#mcp">
                  Connect an agent
                </a>
              </div>
              <div className="dev-trust">
                <span>
                  <b>83</b> cargo airlines
                </span>
                <span>
                  <em>
                    <b>Cargo-IMP</b>
                  </em>
                  &nbsp;normalised
                </span>
                <span>
                  <b>AWB-prefix</b> oracle
                </span>
                <span>
                  <em>
                    <b>MCP</b>
                  </em>
                  -native
                </span>
                <span>
                  live <b>ADS-B</b> position
                </span>
              </div>
            </div>
            <TryIt />
          </section>
        </div>

        <main className="dev-wrap">
          {/* 01 · The surface */}
          <section className="dev-sec" id="surface">
            <div className="dev-sech">
              <span className="num">01</span>
              <h2>The surface</h2>
              <p>REST for your backend, MCP for your agent. Same normalised shape from both.</p>
            </div>
            <div className="dev-console">
              {ENDPOINTS.map(([m, path, desc]) => (
                <div className="dev-row" key={path + m}>
                  <span className={`dev-m ${m === 'POST' ? 'post' : ''}`}>{m}</span>
                  <span className="path">{path}</span>
                  <span className="desc">{desc}</span>
                </div>
              ))}
            </div>
            <p className="dev-cap">
              Base URL <code>https://api.aircargomcp.com</code>. Every response is{' '}
              <code>{'{ "data": ... }'}</code> on success or{' '}
              <code>{'{ "error": { "code", "message" } }'}</code> on failure, a single envelope
              across every endpoint. The <code>/track/:awb</code> and <code>/prefix/:code</code>{' '}
              lookups are open on the demo references below; production keys are Bearer tokens. The
              full request/response contract is published as an{' '}
              <a href="/openapi.json">OpenAPI 3.1 spec</a> you can import into Postman, Insomnia or
              your codegen of choice.
            </p>
          </section>

          {/* 02 · Track by AWB number, the GDS-style single-endpoint deep doc */}
          <section className="dev-sec" id="track">
            <div className="dev-sech">
              <span className="num">02</span>
              <h2>Track by AWB number</h2>
              <p>The primary endpoint, documented end to end: path, params, request, response.</p>
            </div>

            <div
              className="dev-req"
              style={{
                border: '1px solid var(--dev-border)',
                borderRadius: 12,
                background: 'var(--dev-s0)',
              }}
            >
              <span className="dev-meth">GET</span>
              <span className="dev-url">https://api.aircargomcp.com/track/{'{awb}'}</span>
            </div>

            <p className="dev-cap">
              Resolve an air waybill to its live tracking timeline. The AWB is parsed and validated
              (11 digits: a 3-digit airline prefix + 8-digit serial, last digit a mod-7 check), the
              prefix is resolved to an airline via the oracle, and that airline's connector is
              called. Any human format works, <code>203-12345675</code>, <code>203-1234 5675</code>{' '}
              or <code>20312345675</code> all normalise to the same shipment.
            </p>

            <h3 className="dev-slabel">path parameters</h3>
            <div className="dev-paramtable">
              <div className="dev-paramrow">
                <span className="pn">
                  awb <span className="req">required</span>
                </span>
                <span className="pt">string</span>
                <span className="pd">
                  The air waybill number. 3-digit airline prefix + 8-digit serial. Separators and
                  spaces are ignored.
                </span>
              </div>
            </div>

            <h3 className="dev-slabel">headers</h3>
            <div className="dev-paramtable">
              <div className="dev-paramrow">
                <span className="pn">Accept</span>
                <span className="pt">string</span>
                <span className="pd">
                  <code style={{ fontFamily: 'var(--dev-mono)' }}>application/json</code>. Responses
                  are always JSON.
                </span>
              </div>
              <div className="dev-paramrow">
                <span className="pn">Authorization</span>
                <span className="pt">string</span>
                <span className="pd">
                  <code style={{ fontFamily: 'var(--dev-mono)' }}>Bearer YOUR_API_KEY</code> on
                  production keys. Not required for the demo AWB <code>203-12345675</code>.
                </span>
              </div>
            </div>

            <h3 className="dev-slabel">request &amp; response</h3>
            <CodeTabs />

            <h3 className="dev-slabel">response schema · AwbTrackingData</h3>
            <p className="dev-cap" style={{ marginTop: 0, marginBottom: 16 }}>
              On success the <code>data</code> object is one tracked shipment. Types, enums and the
              plain-English meaning of every Cargo-IMP status code:
            </p>
            <div className="dev-schema">
              <SchemaRows fields={SCHEMA} />
            </div>

            <h3 className="dev-slabel">status codes &amp; errors</h3>
            <div className="dev-schema">
              {ERROR_CODES.map(([code, http, d]) => (
                <div className="dev-fld" key={code}>
                  <div className="dev-fld-head">
                    <code className="dev-fname">{code}</code>
                    <span className="dev-ftype">HTTP {http}</span>
                  </div>
                  <div className="dev-fdesc">{d}</div>
                </div>
              ))}
              <div className="dev-fld">
                <div className="dev-fld-head">
                  <code className="dev-fname">200</code>
                  <span className="dev-ftype">HTTP 200</span>
                </div>
                <div className="dev-fdesc">
                  Success. Body is {'{ "data": AwbTrackingData }'} as documented above.
                </div>
              </div>
            </div>
          </section>

          {/* 03 · Four layers */}
          <section className="dev-sec">
            <div className="dev-sech">
              <span className="num">03</span>
              <h2>Four layers, one shipment</h2>
              <p>Track, resolve, locate, price, each a single call, all on the same base URL.</p>
            </div>
            <div className="dev-layers">
              {LAYERS.map(([lbl, nm, desc, viz]) => {
                const Viz = VIZ[viz]
                return (
                  <div className="dev-ch" key={nm}>
                    <div className="lbl">{lbl}</div>
                    <div className="nm">
                      <span className="dev-pip" />
                      {nm}
                    </div>
                    <p>{desc}</p>
                    {Viz && <Viz />}
                  </div>
                )
              })}
            </div>
          </section>

          {/* 04 · The AWB-prefix oracle */}
          <section className="dev-sec">
            <div className="dev-sech">
              <span className="num">04</span>
              <h2>The AWB-prefix oracle</h2>
              <p>
                The 3-digit prefix at the head of every AWB names the airline. Resolve it in one
                call, free and offline, the air equivalent of a container BIC-prefix lookup.
              </p>
            </div>
            <div
              style={{
                display: 'grid',
                gap: 16,
                gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
              }}
            >
              <div className="dev-pbox">
                <PaneHead title="GET /prefix/176, request" code={PREFIX_REQ} />
                <pre className="dev-code" style={{ maxHeight: 'none' }}>
                  <Hl code={PREFIX_REQ} />
                </pre>
              </div>
              <div className="dev-pbox">
                <PaneHead title="200, response" code={PREFIX_RES} />
                <pre className="dev-code" style={{ maxHeight: 'none' }}>
                  <Hl code={PREFIX_RES} />
                </pre>
              </div>
            </div>
            <p className="dev-cap">
              Fields: <code>prefix</code> (3-digit numeric), <code>iata</code> (2-char airline
              code), <code>name</code>, <code>site</code> (the airline's public track-and-trace URL)
              and an optional <code>platform</code>, the shared tracking platform fronting the
              airline (<code>smartkargo</code>, <code>ibs-icargo</code>, <code>dhl-aviation</code>,{' '}
              <code>accelya</code>, <code>hna-portal2</code>, <code>ceair</code>), which is how one
              connector covers many carriers. An unknown prefix returns <code>404 not_found</code>.
            </p>
          </section>

          {/* 05 · Supported airlines — the coverage roster, the air analogue of a carrier list */}
          <section className="dev-sec" id="airlines">
            <div className="dev-sech">
              <span className="num">05</span>
              <h2>Supported airlines</h2>
              <p>
                The AWB-prefix roster we track. Every line here is read from the airline's own
                track-and-trace or its shared platform, and comes back in one normalised shape.
              </p>
            </div>
            <div className="dev-statrow">
              <div className="dev-stat">
                <b>{ROSTER.length}</b>
                <span>airlines · AWB prefixes</span>
              </div>
            </div>
            <div className="dev-airtable">
              <div className="dev-airhead">
                <span>Airline</span>
                <span>IATA</span>
                <span>Prefix</span>
              </div>
              {ROSTER.map(([name, iata, prefix]) => (
                <div className="dev-airrow" key={`${prefix}-${iata}`}>
                  <span className="an">{name}</span>
                  <span className="ai">{iata}</span>
                  <span className="ap">{prefix}</span>
                </div>
              ))}
            </div>
            <p className="dev-cap">
              Each AWB is served by a dedicated airline connector or a shared-platform adapter. A
              prefix outside this list still resolves through <code>GET /prefix/:code</code>, and{' '}
              <code>/track</code> answers it with <code>unsupported_carrier</code>. The roster is
              derived from the router, so it grows the moment a connector lands.
            </p>
          </section>

          {/* 06 · Freighter position + Rate quote */}
          <section className="dev-sec">
            <div className="dev-sech">
              <span className="num">06</span>
              <h2>Where's the plane · what's the rate</h2>
              <p>The live-position layer and the pricing layer, each a single call.</p>
            </div>
            <div className="dev-split">
              <div className="dev-pbox">
                <PaneHead
                  title="GET /aircraft/flight/EK9812 → AirAircraftPosition[]"
                  code={POSITION_RES}
                />
                <pre className="dev-code" style={{ maxHeight: 'none' }}>
                  <Hl code={POSITION_RES} />
                </pre>
              </div>
              <div className="dev-pbox">
                <PaneHead title="POST /rates/quote (HKG→FRA) → AirRateQuote" code={RATE_RES} />
                <pre className="dev-code" style={{ maxHeight: 'none' }}>
                  <Hl code={RATE_RES} />
                </pre>
              </div>
            </div>
            <p className="dev-cap">
              Freighter position is free via ADS-B (<code>adsb.lol</code> / <code>adsb.fi</code>).
              Airborne aircraft only, so a parked or out-of-coverage aircraft returns{' '}
              <code>404 not_found</code>. Rate quotes are real market rates: carrier-specific cards
              from CargoAi when configured, otherwise live Freightos FAX region rates (HKG→FRA,
              PVG→LAX, DXB→LHR and more). Chargeable weight is{' '}
              <code>max(gross, volume_cm³ ÷ 6000)</code>; the per-kg figure falls as the shipment
              crosses the +45 / +100 / +300 / +1000 kg bands.
            </p>
          </section>

          {/* 07 · MCP */}
          <section className="dev-sec" id="mcp">
            <div className="dev-sech">
              <span className="num">07</span>
              <h2>Your agent speaks air cargo</h2>
              <p>One block connects Claude, Cursor or any MCP client to live AWB state.</p>
            </div>
            <div className="dev-mcpline">
              <span className="pr">$</span>
              <span className="cmd">
                npx -y mcp-remote https://mcp.aircargomcp.com/mcp --header "Authorization: Bearer
                YOUR_API_KEY"
              </span>
              <button type="button" className="cp" onClick={copyMcp}>
                {copied ? 'Copied' : 'Copy config'}
              </button>
            </div>
            <p className="dev-cap" style={{ marginBottom: 4 }}>
              Or drop the JSON into your Claude Desktop config:
            </p>
            <div className="dev-pbox" style={{ borderRadius: 12, marginTop: 8 }}>
              <PaneHead title="claude_desktop_config.json" code={MCP_JSON} />
              <pre className="dev-code" style={{ maxHeight: 'none' }}>
                <Hl code={MCP_JSON} />
              </pre>
            </div>
            <div className="dev-tools">
              {TOOLS.map(([t, d]) => (
                <div className="dev-tool" key={t}>
                  <div className="t">{t}</div>
                  <div className="d">{d}</div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="dev-sec">
            <div className="dev-cta">
              <h2>Start with the sandbox. Ship on your own key.</h2>
              <p>
                Track the demo AWB <span className="dev-mono">203-12345675</span> free, right now.
                No card, no account. For a production key with volume pricing, talk to sales.
              </p>
              <div className="r">
                <Link className="dev-btn solid" to="/contact">
                  Contact sales for a quote
                </Link>
                <a className="dev-btn ghost mono" href="#track">
                  Read the API docs
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
    </Layout>
  )
}
